import { Suspense, useState } from 'react';

import { Constants } from '@constants';
import { TAccount } from '@helpers/Accounts/accounts.types';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconOpenDoor } from '@iwoca/icons';
import { Button, Text } from '@iwoca/orion';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { useGetAccessPeriodBannerData } from './useGetAccessPeriodBannerData';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { DrawdownActionPanel } from '../drawdownActionPanel/DrawdownActionPanel';

export const AccessPeriodBanner = ({
    accountId
}: {
    accountId: NonNullable<TAccount['account_id']>;
}) => {
    const [drawdownPanelIsOpen, setDrawdownPanelIsOpen] = useState(false);

    return (
        <ErrorBoundary
            fallback={(error) => <FallbackErrorModal error={error} />}
        >
            <Suspense fallback={<MainContentPending />}>
                <div
                    className="col-span-5 max-sm:col-span-full"
                    data-testid="access-period-banner"
                >
                    <AccessPeriodBannerContent
                        accountId={accountId}
                        setDrawdownPanelIsOpen={setDrawdownPanelIsOpen}
                    />
                    {drawdownPanelIsOpen && (
                        <DrawdownActionPanel
                            accountId={accountId}
                            setDrawdownPanelIsOpen={setDrawdownPanelIsOpen}
                        />
                    )}
                </div>
            </Suspense>
        </ErrorBoundary>
    );
};

const AccessPeriodBannerContent = ({
    accountId,
    setDrawdownPanelIsOpen
}: {
    accountId: NonNullable<TAccount['account_id']>;
    setDrawdownPanelIsOpen: (drawdownPanelIsOpen: boolean) => void;
}) => {
    const { expiryDate, availableCash, accessPeriodStatus } =
        useGetAccessPeriodBannerData(accountId);

    const openDrawdownPanel = () => {
        setDrawdownPanelIsOpen(true);
        tracking.ampli.accessPeriodBannerClicked({
            account_id: accountId,
            access_period_status: accessPeriodStatus
        });
    };

    switch (accessPeriodStatus) {
        case 'OPEN_AVAILABLE_FUNDS':
            return (
                <div className="grid grid-cols-7 gap-m rounded-s border border-success-mid bg-success-lt p-l">
                    <div className="col-span-1 max-sm:col-span-full">
                        <IconOpenDoor className="mx-auto size-4xl text-2xl text-success-drk" />
                    </div>
                    <div className="col-span-6 max-sm:col-span-full max-sm:text-center">
                        <Text className="text-xl font-bold">
                            <FormattedMessage
                                id="_.access_period_banner.available_funds.banner_title"
                                defaultMessage="You can withdraw up to {availableCash, number, :: currency/GBP precision-integer} until {accessPeriodExpiryDate}"
                                values={{
                                    availableCash: availableCash,
                                    accessPeriodExpiryDate: (
                                        <FormattedDate
                                            value={new Date(expiryDate)}
                                            day="2-digit"
                                            month="long"
                                            year="numeric"
                                        />
                                    )
                                }}
                            />
                        </Text>
                        <Text className="text-l">
                            <FormattedMessage
                                id="_.access_period_banner.available_funds.text"
                                defaultMessage=" You’re still within your access period - that means you can still make withdrawals and any repayments you make can be withdrawn again."
                            />
                        </Text>
                    </div>
                    <div className="col-span-full max-sm:text-center min-m:col-start-2">
                        <Button onClick={openDrawdownPanel}>
                            <FormattedMessage
                                id="_.access_period_banner.access_more_funds.cta"
                                defaultMessage="Access more funds"
                            />
                        </Button>
                    </div>
                </div>
            );
        case 'OPEN_EXPIRING_SOON':
            return (
                <div className="grid grid-cols-7 gap-m rounded-s border border-warning bg-warning-lt p-l">
                    <div className="col-span-1 max-sm:col-span-full">
                        <IconOpenDoor className="mx-auto size-4xl text-2xl text-warning-drk" />
                    </div>
                    <div className="col-span-6 max-sm:col-span-full max-sm:text-center">
                        <Text className="text-xl font-bold">
                            <FormattedMessage
                                id="_.access_period_banner.expiring_soon.banner_title"
                                defaultMessage="You can withdraw up to {availableCash, number, :: currency/GBP precision-integer} until {accessPeriodExpiryDate}"
                                values={{
                                    availableCash: availableCash,
                                    accessPeriodExpiryDate: (
                                        <FormattedDate
                                            value={new Date(expiryDate)}
                                            day="2-digit"
                                            month="long"
                                            year="numeric"
                                        />
                                    )
                                }}
                            />
                        </Text>
                        <Text className="text-l">
                            <FormattedMessage
                                id="_.access_period_banner.expiring_soon.text"
                                defaultMessage="Your access period ends in 7 days; Once this ends you would be required to apply again for any further funding. "
                            />
                        </Text>
                    </div>
                    <div className="col-span-full max-sm:text-center min-m:col-start-2">
                        <Button onClick={openDrawdownPanel}>
                            <FormattedMessage
                                id="_.access_period_banner.access_more_funds.cta"
                                defaultMessage="Access more funds"
                            />
                        </Button>
                    </div>
                </div>
            );
        case 'OPEN_NO_FUNDS':
            return (
                <div className="grid grid-cols-7 gap-m rounded-s border border-secondary-80 bg-secondary-95 p-l">
                    <div className="col-span-1 max-sm:col-span-full">
                        <IconOpenDoor className="mx-auto size-4xl text-2xl text-secondary-40" />
                    </div>
                    <div className="col-span-6 max-sm:col-span-full max-sm:text-center">
                        <Text className="text-xl font-bold">
                            <FormattedMessage
                                id="_.access_period_banner.no_available_funds.title"
                                defaultMessage="No available funds to access"
                            />
                        </Text>
                        <Text className="text-l">
                            <FormattedMessage
                                id="_.access_period_banner.no_available_funds.text"
                                defaultMessage="You’re still within your access period - however you have less than {minimumDrawdownAmount, number, :: currency/GBP precision-integer} which is below our minimum withdrawal amount. "
                                values={{
                                    minimumDrawdownAmount:
                                        Constants.MIN_DRAW_DOWN_AMOUNT
                                }}
                            />
                        </Text>
                    </div>
                </div>
            );
        case 'EXPIRED':
        default:
            return null;
    }
};
